import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  ArrowsUpDownIcon,
  XMarkIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import api from '../../utils/api';

const ProjectFields = () => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingFieldId, setEditingFieldId] = useState(null);
  const [name, setName] = useState('');
  const [fieldType, setFieldType] = useState('alphanumeric');
  const [description, setDescription] = useState('');
  const [isRequired, setIsRequired] = useState(false);
  const [displayOrder, setDisplayOrder] = useState(0);
  const [options, setOptions] = useState('');  // For dropdown options as comma-separated values
  const [validationRules, setValidationRules] = useState({});

  // Fetch field definitions
  const fetchFields = async () => {
    setLoading(true);
    try {
      const response = await api.get('/settings/project-fields');
      setFields(response.data.fields || []);
    } catch (err) {
      console.error('Error fetching project fields:', err);
      setError('Failed to load project fields. Please try again.');
      toast.error('Failed to load project fields');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFields();
  }, []);

  // Reset form state
  const resetForm = () => {
    setName('');
    setFieldType('alphanumeric');
    setDescription('');
    setIsRequired(false);
    setDisplayOrder(fields.length);
    setOptions('');
    setValidationRules({});
    setEditingFieldId(null);
  };

  // Handle form submission for create/update
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const fieldData = {
        name,
        field_type: fieldType,
        description,
        is_required: isRequired,
        display_order: displayOrder,
        options: fieldType === 'dropdown' ? { values: options.split(',').map(opt => opt.trim()) } : {},
        validation_rules: validationRules
      };
      
      let response;
      
      if (editingFieldId) {
        // Update existing field
        response = await api.put(`/settings/project-fields/${editingFieldId}`, fieldData);
        toast.success('Field updated successfully');
      } else {
        // Create new field
        response = await api.post('/settings/project-fields', fieldData);
        toast.success('Field created successfully');
      }
      
      fetchFields();
      setShowAddForm(false);
      resetForm();
    } catch (err) {
      console.error('Error saving field:', err);
      toast.error(err.response?.data?.message || 'Failed to save field');
    }
  };

  // Handle editing a field
  const handleEdit = (field) => {
    setEditingFieldId(field.id);
    setName(field.name);
    setFieldType(field.field_type);
    setDescription(field.description || '');
    setIsRequired(field.is_required);
    setDisplayOrder(field.display_order);
    
    if (field.field_type === 'dropdown' && field.options?.values) {
      setOptions(field.options.values.join(', '));
    } else {
      setOptions('');
    }
    
    setValidationRules(field.validation_rules || {});
    setShowAddForm(true);
  };

  // Handle deleting a field
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this field? This action cannot be undone.')) {
      return;
    }
    
    try {
      await api.delete(`/settings/project-fields/${id}`);
      toast.success('Field deleted successfully');
      fetchFields();
    } catch (err) {
      console.error('Error deleting field:', err);
      toast.error('Failed to delete field');
    }
  };

  // Toggle field active status
  const toggleActive = async (field) => {
    try {
      await api.patch(`/settings/project-fields/${field.id}`, {
        is_active: !field.is_active
      });
      toast.success(`Field ${field.is_active ? 'deactivated' : 'activated'} successfully`);
      fetchFields();
    } catch (err) {
      console.error('Error toggling field status:', err);
      toast.error('Failed to update field status');
    }
  };

  const fieldTypeOptions = [
    { value: 'alphanumeric', label: 'Alphanumeric' },
    { value: 'date', label: 'Date' },
    { value: 'text', label: 'Text Only' },
    { value: 'numeric', label: 'Numeric Only' },
    { value: 'dropdown', label: 'Dropdown' }
  ];

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">Project Fields</h1>
          <p className="mt-2 text-sm text-gray-700">
            Define custom fields for project plans. These fields will be available when creating project plans for clients.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            type="button"
            onClick={() => {
              resetForm();
              setShowAddForm(true);
            }}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
            Add Field
          </button>
        </div>
      </div>

      {/* Form for adding/editing fields */}
      {showAddForm && (
        <div className="mt-8 bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              {editingFieldId ? 'Edit Field' : 'Add New Field'}
            </h3>
            <div className="mt-2 max-w-xl text-sm text-gray-500">
              <p>Define the properties for this project field.</p>
            </div>
            <form className="mt-5 space-y-4" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                <div className="sm:col-span-3">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Field Name <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      name="name"
                      id="name"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="field-type" className="block text-sm font-medium text-gray-700">
                    Field Type <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <select
                      id="field-type"
                      name="field-type"
                      required
                      value={fieldType}
                      onChange={(e) => setFieldType(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    >
                      {fieldTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="sm:col-span-6">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <div className="mt-1">
                    <textarea
                      id="description"
                      name="description"
                      rows={3}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>
                  <p className="mt-2 text-sm text-gray-500">Brief description for this field.</p>
                </div>

                {fieldType === 'dropdown' && (
                  <div className="sm:col-span-6">
                    <label htmlFor="options" className="block text-sm font-medium text-gray-700">
                      Dropdown Options <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="options"
                        id="options"
                        required
                        value={options}
                        onChange={(e) => setOptions(e.target.value)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        placeholder="Option 1, Option 2, Option 3..."
                      />
                    </div>
                    <p className="mt-2 text-sm text-gray-500">Enter options as comma-separated values.</p>
                  </div>
                )}

                <div className="sm:col-span-3">
                  <label htmlFor="display-order" className="block text-sm font-medium text-gray-700">
                    Display Order
                  </label>
                  <div className="mt-1">
                    <input
                      type="number"
                      name="display-order"
                      id="display-order"
                      min="0"
                      value={displayOrder}
                      onChange={(e) => setDisplayOrder(parseInt(e.target.value, 10))}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <div className="flex items-start pt-5">
                    <div className="flex items-center h-5">
                      <input
                        id="is-required"
                        name="is-required"
                        type="checkbox"
                        checked={isRequired}
                        onChange={(e) => setIsRequired(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="is-required" className="font-medium text-gray-700">
                        Required Field
                      </label>
                      <p className="text-gray-500">Make this field mandatory for project plans.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    resetForm();
                  }}
                  className="rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  {editingFieldId ? 'Update Field' : 'Add Field'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Fields table */}
      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              {loading ? (
                <div className="px-6 py-8 text-center text-gray-500">
                  <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-500 mx-auto"></div>
                  <p className="mt-2">Loading field definitions...</p>
                </div>
              ) : error ? (
                <div className="px-6 py-8 text-center text-red-500">
                  <p>{error}</p>
                  <button 
                    onClick={fetchFields} 
                    className="mt-2 text-indigo-600 hover:text-indigo-900"
                  >
                    Try again
                  </button>
                </div>
              ) : fields.length === 0 ? (
                <div className="px-6 py-8 text-center text-gray-500">
                  <p>No field definitions found. Click "Add Field" to create your first field.</p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                        Name
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Type
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Required
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Order
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Status
                      </th>
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {fields.map((field) => (
                      <tr 
                        key={field.id} 
                        className={field.is_active ? '' : 'bg-gray-50 text-gray-500'}
                      >
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                          {field.name}
                          {field.description && (
                            <p className="text-xs text-gray-500 mt-1 max-w-xs truncate">{field.description}</p>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {fieldTypeOptions.find(opt => opt.value === field.field_type)?.label || field.field_type}
                          </span>
                          {field.field_type === 'dropdown' && field.options?.values && (
                            <div className="text-xs text-gray-500 mt-1 max-w-xs truncate">
                              Options: {field.options.values.join(', ')}
                            </div>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {field.is_required ? (
                            <CheckIcon className="h-5 w-5 text-green-500" />
                          ) : (
                            <XMarkIcon className="h-5 w-5 text-gray-400" />
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {field.display_order}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          <span 
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              field.is_active 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {field.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                          <div className="flex justify-end space-x-3">
                            <button
                              onClick={() => handleEdit(field)}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              <PencilIcon className="h-5 w-5" aria-hidden="true" />
                              <span className="sr-only">Edit {field.name}</span>
                            </button>
                            <button
                              onClick={() => toggleActive(field)}
                              className="text-gray-600 hover:text-gray-900"
                              title={field.is_active ? 'Deactivate' : 'Activate'}
                            >
                              {field.is_active ? (
                                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                              ) : (
                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                              )}
                              <span className="sr-only">
                                {field.is_active ? 'Deactivate' : 'Activate'} {field.name}
                              </span>
                            </button>
                            <button
                              onClick={() => handleDelete(field.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <TrashIcon className="h-5 w-5" aria-hidden="true" />
                              <span className="sr-only">Delete {field.name}</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectFields; 